package ai.wagemix.web.screens.share

import ai.wagemix.net.models.WMShareResult
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.share.WMBadgePreview
import ai.wagemix.logic.share.WMDefaultShareMixPresenter
import ai.wagemix.logic.share.WMPayRangeProblem
import ai.wagemix.logic.share.WMShareMixPresenter
import ai.wagemix.logic.share.WMShareMixPresenter.Action
import ai.wagemix.logic.share.WMShareMixPresenter.Event
import ai.wagemix.logic.share.WMShareMixPresenter.Prompt
import ai.wagemix.logic.share.WMSharePreviewState
import ai.wagemix.logic.share.WMShareTarget
import ai.wagemix.net.models.WMBadge
import ai.wagemix.net.models.WMSharedMixPurpose
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMCheckbox
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMField
import ai.wagemix.web.components.WMFieldRow
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMNotice
import ai.wagemix.web.components.WMNoticeKind
import ai.wagemix.web.components.WMSegmentedControl
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.components.WMTierBadge
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCardCornerVar
import ai.wagemix.web.theme.WMLabelTextStyle
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import ai.wagemix.web.screens.WMPromptDialog
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewModel
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewStateHolder
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.px

interface WMShareMixWebViewStateHolder<out P: Prompt>: FSWebPromptingViewStateHolder<P> {
    val targetState: State<WMShareTarget?>
    val badgePreviewState: State<WMBadgePreview?>
    val resultState: State<WMShareResult?>
    val previewImageState: State<String?>
    val previewStateState: State<WMSharePreviewState?>
}

abstract class WMBaseShareMixWebViewModel<P: Prompt, E: Event<P>, A: Action, PR: WMShareMixPresenter<P, E, A>>(
    presenter: PR,
    protected val externalNavigation: (WMNavigationRequest) -> Unit
): FSWebPromptingViewModel<P, E, A, PR>(presenter), WMShareMixWebViewStateHolder<P> {

    override val targetState: State<WMShareTarget?> get() = _targetState
    protected val _targetState = mutableStateOf<WMShareTarget?>(null)

    override val badgePreviewState: State<WMBadgePreview?> get() = _badgePreviewState
    protected val _badgePreviewState = mutableStateOf<WMBadgePreview?>(null)

    override val resultState: State<WMShareResult?> get() = _resultState
    protected val _resultState = mutableStateOf<WMShareResult?>(null)

    override val previewImageState: State<String?> get() = _previewImageState
    protected val _previewImageState = mutableStateOf<String?>(null)

    override val previewStateState: State<WMSharePreviewState?> get() = _previewStateState
    protected val _previewStateState = mutableStateOf<WMSharePreviewState?>(null)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            targetUpdate?.let { _targetState.value = it }
            badgePreviewUpdate?.let { _badgePreviewState.value = it }
            resultUpdate?.let { _resultState.value = it.value }
            previewImageUpdate?.let { _previewImageState.value = it }
            previewStateUpdate?.let { _previewStateState.value = it }
            navigationUpdate?.let { externalNavigation(it) }
        }
    }

    fun userEditedForm(hourly: Boolean, low: Double?, high: Double?) = presenter.userEditedForm(hourly, low, high)
    fun userRequestedCreate(hourly: Boolean, low: Double?, high: Double?, purpose: WMSharedMixPurpose) = presenter.userRequestedCreate(hourly, low, high, purpose)
    fun userRequestedReset() = presenter.userRequestedReset()
    fun userRequestedRegeneratePreview() = presenter.userRequestedRegeneratePreview()
}

typealias WMDefaultShareMixWebViewModel = WMBaseShareMixWebViewModel<Prompt.Base, Event.Base<Prompt.Base>, Action.Base, WMDefaultShareMixPresenter>

fun createDefaultShareMixWebViewModel(
    presenter: WMDefaultShareMixPresenter,
    externalNavigation: (WMNavigationRequest) -> Unit
): WMDefaultShareMixWebViewModel = object: WMDefaultShareMixWebViewModel(presenter, externalNavigation) {
    override val defaultPrompt: Prompt.Base
        get() = Prompt.baseFactory().none()
}

/** Penpot `Dialog / Share mix` (configure → link ready), with its failure prompts. */
@Composable
fun <P: Prompt, E: Event<P>, A: Action, PR: WMShareMixPresenter<P, E, A>, VM: WMBaseShareMixWebViewModel<P, E, A, PR>> WMShareMixDialog(
    createViewModel: () -> VM,
    mixId: String,
    strings: WMWebStrings,
    onClose: () -> Unit
) {
    FSWebScreen(createViewModel = createViewModel, key = mixId) { vm ->
        WMShareMixDialogLayout(
            strings = strings,
            loadingState = vm.loadingState,
            targetState = vm.targetState,
            badgePreviewState = vm.badgePreviewState,
            resultState = vm.resultState,
            previewImageState = vm.previewImageState,
            previewStateState = vm.previewStateState,
            onFormChange = vm::userEditedForm,
            onCreate = vm::userRequestedCreate,
            onReset = vm::userRequestedReset,
            onRegeneratePreview = vm::userRequestedRegeneratePreview,
            onClose = onClose
        )
        val prompt = vm.promptState.value
        WMPromptDialog(prompt = prompt, unwrap = { (it as? Prompt.ComposedSuperPrompt)?.wrapped }, strings = strings, onResponse = { vm.userRespondedToPrompt(FSPrompt.Response(prompt, it)) }) { p ->
            val respond = { i: Int -> vm.userRespondedToPrompt(FSPrompt.Response(prompt, i)) }
            when (p) {
                is Prompt.ShareFailed -> WMDialog(
                    title = strings.share.failedTitle,
                    description = p.message ?: strings.share.failedMessage,
                    onDismiss = { respond(Prompt.ShareFailed.Selection.Dismiss.ordinal) },
                    actions = { WMButton(strings.common.close, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.ShareFailed.Selection.Dismiss.ordinal) } }
                ) {}
                is Prompt.PreviewRegenerationFailed -> WMDialog(
                    title = strings.share.regenerateFailedTitle,
                    description = p.message ?: strings.share.regenerateFailedMessage,
                    onDismiss = { respond(Prompt.PreviewRegenerationFailed.Selection.Dismiss.ordinal) },
                    actions = {
                        WMButton(strings.common.close, modifier = WMDialogButtonStyle.toModifier()) {
                            respond(Prompt.PreviewRegenerationFailed.Selection.Dismiss.ordinal)
                        }
                    }
                ) {}
                is Prompt.SessionExpired -> WMDialog(
                    title = strings.share.sessionExpiredTitle,
                    description = strings.share.sessionExpiredMessage,
                    onDismiss = { respond(Prompt.SessionExpired.Selection.Dismiss.ordinal) },
                    forceInteraction = true,
                    actions = {
                        WMButton(strings.common.dismiss, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.SessionExpired.Selection.Dismiss.ordinal); onClose() }
                        WMButton(strings.common.signIn, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.SessionExpired.Selection.SignIn.ordinal) }
                    }
                ) {}
                else -> Unit
            }
        }
    }
}

@Composable
fun WMShareMixDialogLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    targetState: State<WMShareTarget?>,
    badgePreviewState: State<WMBadgePreview?>,
    resultState: State<WMShareResult?>,
    previewImageState: State<String?>,
    previewStateState: State<WMSharePreviewState?>,
    onFormChange: (Boolean, Double?, Double?) -> Unit,
    onCreate: (Boolean, Double?, Double?, WMSharedMixPurpose) -> Unit,
    onReset: () -> Unit,
    onRegeneratePreview: () -> Unit,
    onClose: () -> Unit
) {
    val sh = strings.share
    val colors = ColorMode.current.toPalette().fs
    val target = targetState.value
    val result = resultState.value
    var hourly by remember(target) { mutableStateOf(target?.hourly == true) }
    var lowText by remember { mutableStateOf("") }
    var highText by remember { mutableStateOf("") }
    var forPosting by remember(target) { mutableStateOf(target?.fromJobDescription == true) }
    var copied by remember(result) { mutableStateOf(false) }
    val low = lowText.filter { it.isDigit() || it == '.' }.toDoubleOrNull()
    val high = highText.filter { it.isDigit() || it == '.' }.toDoubleOrNull()
    val preview = badgePreviewState.value

    if (result == null) {
        WMDialog(
            title = sh.title,
            description = target?.title?.let { "${sh.description} ($it)" } ?: sh.description,
            onDismiss = onClose,
            actions = {
                WMButton(strings.common.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier(), onClick = onClose)
                WMButton(
                    sh.create,
                    modifier = WMDialogButtonStyle.toModifier(),
                    enabled = target != null && !loadingState.value && preview?.problem == null,
                    leadingIcon = WMIconGlyph.Link
                ) { onCreate(hourly, low, high, if (forPosting) WMSharedMixPurpose.JD else WMSharedMixPurpose.Social) }
            }
        ) {
            Column(modifier = Modifier.fillMaxWidth().gap(16.px)) {
                WMSegmentedControl(
                    options = listOf(strings.common.annual, strings.common.hourly),
                    selectedIndex = if (hourly) 1 else 0,
                    onSelect = { hourly = it == 1; onFormChange(hourly, low, high) },
                    accessibilityString = "${strings.common.annual} / ${strings.common.hourly}",
                    modifier = Modifier.maxWidth(240.px)
                )
                WMField(
                    sh.payRange,
                    helperText = sh.payRangeHelper,
                    errorText = when (preview?.problem) {
                        WMPayRangeProblem.LowAboveHigh -> sh.lowAboveHigh
                        WMPayRangeProblem.NotPositive -> sh.notPositive
                        null -> null
                    }
                ) {
                    WMFieldRow {
                        WMTextField(lowText, onTextChange = { lowText = it; onFormChange(hourly, it.toDoubleOrNull(), high) }, placeholder = sh.low, valid = preview?.problem == null)
                        WMTextField(highText, onTextChange = { highText = it; onFormChange(hourly, low, it.toDoubleOrNull()) }, placeholder = sh.high, valid = preview?.problem == null)
                    }
                }
                Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                    SpanText(sh.badgePreview, WMLabelTextStyle.toModifier().color(colors.onSurfaceVariant))
                    val badge = preview?.badge
                    when {
                        preview?.statsReady == false -> SpanText(sh.statsLoading, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
                        badge != null && badge != WMBadge.None -> WMTierBadge(badge, sh.tierName(badge), sh.tierExplanation(badge))
                        badge == WMBadge.None -> WMNotice(sh.noBadge, kind = WMNoticeKind.Info)
                        else -> SpanText(sh.payRangeHelper, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
                    }
                }
                WMCheckbox(checked = forPosting, onCheckedChange = { forPosting = it }, label = sh.forJobPosting)
            }
        }
    } else {
        WMDialog(
            title = sh.linkReady,
            description = sh.linkReadyDescription,
            onDismiss = onClose,
            actions = {
                WMButton(sh.shareAnother, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier(), onClick = onReset)
                WMButton(sh.openLink, kind = WMButtonKind.Secondary, modifier = WMDialogButtonStyle.toModifier(), leadingIcon = WMIconGlyph.ExternalLink) { kotlinx.browser.window.open(result.shareableUrl, "_blank") }
                WMButton(if (copied) sh.copied else sh.copy, modifier = WMDialogButtonStyle.toModifier(), leadingIcon = if (copied) WMIconGlyph.Check else WMIconGlyph.Copy) {
                    kotlinx.browser.window.navigator.clipboard.writeText(result.shareableUrl)
                    copied = true
                }
            }
        ) {
            Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
                WMTextField(result.shareableUrl, onTextChange = {}, readOnly = true, leadingIcon = WMIconGlyph.Link)
                WMSharePreviewImage(
                    strings = strings,
                    state = previewStateState.value,
                    imageUrl = previewImageState.value,
                    onRegenerate = onRegeneratePreview
                )
            }
        }
    }
}
